import React, { Dispatch, SetStateAction } from 'react';
import { Dropdown, Form } from 'react-bootstrap';

import { UseOutletContextAccounting } from '../Accounting';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { format, startOfMonth, startOfWeek, startOfYear, subDays } from 'date-fns';

export interface AppAccountingFilterIntervalProps {
  storage: any,
  filter: {interval: string, start_date: string, end_date: string} | {booking_journal: string, options: {accounting_account: string, interval: string, start_date: string, end_date: string}},
  setFilter: Dispatch<SetStateAction<any>>,
  loadAccountingEntries: Function,
  includeOpeningBalance?: boolean,
  onIncludeOpeningBalanceChange?: (value: boolean) => void
};

const AppAccountingFilterInterval: React.FunctionComponent<AppAccountingFilterIntervalProps> = ({storage, filter, setFilter, loadAccountingEntries, includeOpeningBalance, onIncludeOpeningBalanceChange}) => {
  const {companyForUser} = UseOutletContextAccounting()
  const {lang} = StateLanguage()

  const handleChangeInterval = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    let startDate: Date = new Date()
    let endDate: Date = new Date()
    let interval: string = ''
    let start: string = ''
    let end: string = ''

    if (companyForUser) {
      switch (event.target.value) {
        case "today":
          interval = event.target.value
          start = format(startDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
          end = format(endDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
          break
        case "yesterday":
          interval = event.target.value
          start = format(subDays(startDate, 1), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
          end = format(subDays(endDate, 1), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
          break
        case "week":
          interval = event.target.value
          start = format(startOfWeek(startDate), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
          end = format(endDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
          break
        case "month":
          interval = event.target.value
          start = format(startOfMonth(startDate), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
          end = format(endDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
          break
        case "year":
          interval = event.target.value
          start = format(startOfYear(startDate), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
          end = format(endDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
          break
        default:
          interval = 'all'
          start = format(new Date(companyForUser.created_at), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
          end = format(endDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
      }
    }

    if ("options" in filter) {
      setFilter({...filter, options: {...filter.options, interval: interval, start_date: start, end_date: end}})
    } else {
      setFilter({...filter, interval: interval, start_date: start, end_date: end})
    }
  }

  const handleChangeStartDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let date: string = (event.target.value.length > 0) ? format((new Date(`${event.target.value}T00:00:00.000`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : ''

    if ("options" in filter) {
      setFilter({...filter, options: {...filter.options, interval: '', start_date: date}})
    } else {
      setFilter({...filter, interval: '', start_date: date})
    }
  }

  const handleChangeEndDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let date: string = (event.target.value.length > 0) ? format((new Date(`${event.target.value}T23:59:59.999`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : ''

    if ("options" in filter) {
      setFilter({...filter, options: {...filter.options, interval: '', end_date: date}})
    } else {
      setFilter({...filter, interval: '', end_date: date})
    }
  }

  const executeFilterApply = () => {
    loadAccountingEntries()
  }

  const executeFilterReset = () => {
    if ("options" in filter) {
      filter.options.interval = ''
      filter.options.start_date = ''
      filter.options.end_date = ''
    } else {
      filter.interval = ''
      filter.start_date = ''
      filter.end_date = ''
    }
    if (onIncludeOpeningBalanceChange) onIncludeOpeningBalanceChange(true)
    loadAccountingEntries()
  }

  return (
    <>
      { companyForUser &&
        <Dropdown>
          <Dropdown.Toggle variant="default btn-sm btn-light-primary d-flex align-items-center justify-content-between ps-3 pe-1 w-200px">
            <div className="fw-bold">{dateFormat(lang.code, storage ? storage.start_date : companyForUser.created_at)} - {dateFormat(lang.code, storage ? storage.end_date : `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`)}</div>
            <span className="svg-icon svg-icon-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black"></path>
              </svg>
            </span>
          </Dropdown.Toggle>
          <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown w-250px w-md-300px" align="end">
            <div className="mt-5 mx-5">
              <div className="fs-5 text-dark fw-bolder">{lang.labels.filterOptions}</div>
            </div>
            <div className="separator border-gray-200"></div>
            <div className="mt-3 m-5">
              <div className="mb-3">
                <label className="form-label fs-7">{lang.labels.timeInterval}:</label>
                <Form.Select bsPrefix="form-select form-select-sm fw-bold" aria-placeholder={lang.labels.select} name="filter-interval" value={"options" in filter ? filter.options.interval : filter.interval} onChange={handleChangeInterval}>
                  <option value="" className="d-none">{lang.labels.select}</option>
                  <option value="all">{lang.labels.all}</option>
                  <option value="year">{lang.labels.thisYear}</option>
                  <option value="month">{lang.labels.thisMonth}</option>
                  <option value="week">{lang.labels.thisWeek}</option>
                  <option value="yesterday">{lang.labels.yesterday}</option>
                  <option value="today">{lang.labels.today}</option>
                </Form.Select>
              </div>
              <div className="d-md-flex align-items-center mb-3">
                <label className="col-12 col-md-5 col-form-label fs-7 py-2">{lang.labels.startDate}:</label>
                <input className="col-12 col-md-7 small-input bg-light border border-light rounded" type="date" name="filter-start-date" max={("options" in filter ? filter.options : filter).end_date.substring(0, 10)} value={("options" in filter ? filter.options : filter).start_date.substring(0, 10)} onChange={handleChangeStartDate} />
              </div>
              <div className="d-md-flex align-items-center mb-3">
                <label className="col-12 col-md-5 col-form-label fs-7 py-2">{lang.labels.endDate}:</label>
                <input className="col-12 col-md-7 small-input bg-light border border-light rounded" type="date" name="filter-end-date" min={("options" in filter ? filter.options : filter).start_date.substring(0, 10)} value={("options" in filter ? filter.options : filter).end_date.substring(0, 10)} onChange={handleChangeEndDate} />
              </div>
              { onIncludeOpeningBalanceChange && ("options" in filter ? filter.options : filter).start_date.length > 0 &&
                <div className="d-flex align-items-center mb-5">
                  <Form.Check
                    type="checkbox"
                    id="filter-include-opening-balance"
                    label={lang.labels.includeOpeningBalance}
                    className="fs-7"
                    checked={includeOpeningBalance !== false}
                    onChange={(e) => onIncludeOpeningBalanceChange(e.target.checked)}
                  />
                </div>
              }
              <div className="d-flex justify-content-end">
                <Dropdown.Item bsPrefix="btn btn-sm btn-light btn-active-light-primary" onClick={executeFilterReset}>{lang.labels.reset}</Dropdown.Item>
                <Dropdown.Item bsPrefix="btn btn-sm btn-primary ms-3" onClick={executeFilterApply}>{lang.labels.apply}</Dropdown.Item>
              </div>
            </div>
          </Dropdown.Menu>
        </Dropdown>
      }
    </>
  )
};

export default AppAccountingFilterInterval;
